import React from 'react';
import { ROUTES } from '../constants/app';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console and potentially to an error reporting service
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = ROUTES.HOME;
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <h1>Oops! Something went wrong</h1>
            <p>We're sorry, but something unexpected happened.</p>
            
            <div className="error-actions">
              <button onClick={this.handleReload} className="error-button primary">
                Reload Page
              </button>
              <button onClick={this.handleGoHome} className="error-button secondary">
                Go to Home
              </button>
            </div>
            
            {process.env.NODE_ENV === 'development' && (
              <details className="error-details">
                <summary>Error Details (Development Only)</summary>
                <div className="error-stack">
                  <h3>Error:</h3>
                  <pre>{this.state.error && this.state.error.toString()}</pre>
                  
                  <h3>Component Stack:</h3>
                  <pre>{this.state.errorInfo.componentStack}</pre>
                </div>
              </details>
            )}
          </div>
          
          <style jsx>{`
            .error-boundary {
              min-height: 100vh;
              display: flex;
              align-items: center;
              justify-content: center;
              background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
              font-family: 'Poppins', sans-serif;
              padding: 20px;
            }
            
            .error-boundary-content {
              background: white;
              border-radius: 12px;
              padding: 40px;
              max-width: 600px;
              text-align: center;
              box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
            }
            
            .error-boundary-content h1 {
              color: #333;
              margin-bottom: 16px;
              font-size: 28px;
            }
            
            .error-boundary-content p {
              color: #666;
              margin-bottom: 30px;
              font-size: 16px;
              line-height: 1.5;
            }
            
            .error-actions {
              display: flex;
              gap: 16px;
              justify-content: center;
              margin-bottom: 30px;
            }
            
            .error-button {
              padding: 12px 24px;
              border: none;
              border-radius: 8px;
              font-size: 16px;
              font-weight: 600;
              cursor: pointer;
              transition: all 0.3s ease;
              font-family: inherit;
            }
            
            .error-button.primary {
              background: linear-gradient(135deg, #4169E1, #00CED1);
              color: white;
            }
            
            .error-button.primary:hover {
              transform: translateY(-2px);
              box-shadow: 0 4px 15px rgba(65, 105, 225, 0.3);
            }
            
            .error-button.secondary {
              background: #f8f9fa;
              color: #333;
              border: 2px solid #dee2e6;
            }
            
            .error-button.secondary:hover {
              background: #e9ecef;
              transform: translateY(-2px);
            }
            
            .error-details {
              text-align: left;
              margin-top: 20px;
              padding: 20px;
              background: #f8f9fa;
              border-radius: 8px;
              border: 1px solid #dee2e6;
            }
            
            .error-details summary {
              cursor: pointer;
              font-weight: 600;
              color: #495057;
              margin-bottom: 16px;
            }
            
            .error-stack {
              font-size: 12px;
            }
            
            .error-stack h3 {
              color: #495057;
              margin: 16px 0 8px 0;
              font-size: 14px;
            }
            
            .error-stack pre {
              background: #ffffff;
              border: 1px solid #dee2e6;
              border-radius: 4px;
              padding: 12px;
              overflow-x: auto;
              white-space: pre-wrap;
              word-wrap: break-word;
              color: #e83e8c;
              font-size: 11px;
              line-height: 1.4;
            }
            
            @media (max-width: 768px) {
              .error-boundary-content {
                padding: 30px 20px;
                margin: 0 10px;
              }
              
              .error-actions {
                flex-direction: column;
              }
              
              .error-button {
                width: 100%;
              }
            }
          `}</style>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
